import { useRefreshCandidate } from "#candidate/redux/actions"
import { candidatePost } from "#candidate/services/api"
import InputDateTime from "#common/input/InputDateTime"
import { DialogMaster } from "#core/components/dialog/Dialog"
import InputNumber from "#core/components/input/number/InputNumber"
import { cleanSpaces, isValidEmail } from "#core/functions/text"
import { useApiSend } from "#hooks/useApiSend"
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import { Box, Button, Stack, TextField, Tooltip, Typography } from "@mui/material"
import { useCallback, useState } from "react"
import { toast } from "react-toastify"

const defaultValue = {
	name: "",
	paternal_last_name: "",
	maternal_last_name: "",
	correo: "",
	celular: "",
	interview_date_rh: null,
}

export default function CandidateForm({ candidate, setCandidate }) {
	const register = (nombre) => {
		return {
			id: nombre,
			value: candidate?.[nombre],
			onChange: (e) => setCandidate((old) => ({ ...old, [nombre]: e.target.value })),
		}
	}

	return (
		<Box display="flex" gap={1} flexDirection="column">
			<TextField label={"Nombre"} {...register("name")} size="small" />
			<TextField
				label={"Apellido Paterno"}
				{...register("paternal_last_name")}
				size="small"
			/>
			<TextField
				label={"Apellido Materno"}
				{...register("maternal_last_name")}
				size="small"
			/>
			<TextField label={"Correo"} {...register("correo")} size="small" />
			<InputNumber
				label={"Teléfono"}
				{...register("celular")}
				setValue={(value) => setCandidate((old) => ({ ...old, celular: value }))}
				size="small"
				isInt
			/>
			<InputDateTime
				{...{ label: "Fecha de entrevista RH", value: candidate.interview_date_rh }}
				onChange={(value) =>
					setCandidate((old) => ({ ...old, interview_date_rh: value }))
				}
				size="small"
			/>
			<InputFilePDF
				value={candidate.resume}
				setValue={(value) => setCandidate((old) => ({ ...old, resume: value }))}
				icon={<PictureAsPdfIcon />}
				label={"curriculum vitae"}
			/>
		</Box>
	)
}

export const CandidateFormDialog = ({ open, setOpen, profileData }) => {
	const { refresh } = useRefreshCandidate()
	const [candidate, setCandidate] = useState(defaultValue)
	const [disabled, setDisabled] = useState(false)
	const success = () => {
		refresh()
		setCandidate(defaultValue)
		setOpen(false)
	}
	const end = () => {
		setDisabled(false)
	}
	const { fetch } = useApiSend(candidatePost, { success, end })

	const handleSubmit = useCallback(() => {
		if (!candidateFormValidation(candidate)) return
		setDisabled(true)
		let formData = new FormData()
		for (const key in candidate) {
			if (!Object.hasOwn(candidate, key)) continue
			let element = candidate[key]
			if (key === "interview_date_rh" && element) {
				element = element.toISOString() // o .toISOString()
			}
			formData.append(key, element)
		}
		formData.append("vacante", profileData.id)

		fetch(formData)
	}, [candidate, fetch, profileData.id])

	return (
		<DialogMaster
			{...{
				open,
				setOpen,
				maxWidth: "sm",
				title: "Nuevo Candidato",
				disabled,
				handleSubmit,
			}}
		>
			<CandidateForm {...{ candidate, setCandidate }} />
		</DialogMaster>
	)
}

const InputFilePDF = ({ label, icon, value, setValue }) => {
	const fileChange = (e) => {
		const files = e.target.files
		if (files.length === 0) setValue(null)
		else setValue(files[0])
	}

	return (
		<Stack spacing={2} alignItems="center" sx={{ width: "100%" }}>
			<Button startIcon={icon} variant="contained" fullWidth component="label">
				{label}
				<input type="file" hidden onChange={fileChange} accept=".pdf" />
			</Button>

			{value?.name && (
				<Tooltip title={value.name}>
					<Stack
						direction="row"
						alignItems="center"
						sx={{
							maxWidth: "100%",
							overflow: "hidden",
						}}
						spacing={1}
					>
						<InsertDriveFileIcon fontSize="small" />

						<Typography variant="body2" noWrap sx={{ textOverflow: "ellipsis" }}>
							{value.name}
						</Typography>
					</Stack>
				</Tooltip>
			)}
		</Stack>
	)
}

const candidateFormValidation = (data) => {
	const clearData = [
		{ key: "name", label: "Nombre" },
		{ key: "correo", label: "Correo" },
		{ key: "paternal_last_name", label: "Apellido Paterno" },
		{ key: "maternal_last_name", label: "Apellido Materno" },
	]

	clearData.forEach((field) => {
		if (typeof data[field.key] === "string")
			data[field.key] = cleanSpaces(data[field.key])
	})

	if (!data.name) {
		toast.error("Se requiere nombre del candidato")
		return
	}
	if (!(data.paternal_last_name || data.maternal_last_name)) {
		toast.error("Se requiere un apellido")
		return
	}
	if (!data.celular) {
		toast.error("Se requiere teléfono del candidato")
		return
	}
	if (!data.resume) {
		toast.error("Se requiere correo cv del candidato")
		return
	}
	if (!data.correo) {
		toast.error("Se requiere correo del candidato")
		return
	}
	if (!isValidEmail(data.correo)) {
		toast.error("Correo no valido")
		return
	}
	if (data.interview_date_rh && !data.interview_date_rh.isValid()) {
		toast.error("Fecha de entrevista no válida")
		return
	}
	return true
}
